import { Spinner } from "@/components/ui/spinner";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { RecurringFrequency, TaxIncomeType, IncomeCategory } from "@boo/shared";
import { useRecurringIncomeStore } from "../../store/useRecurringIncomeStore";
import type { CreateRecurringIncomePayload } from "../../../../services/recurringIncomeApi";
import { INCOME_CATEGORY_LABELS, INCOME_CATEGORY_COLORS } from "../../types";
import AmountInput from "@/components/AmountInput";
import { useAccountStore } from "@/features/accounts/store/useAccountStore";

const INCOME_CATEGORIES: IncomeCategory[] = [
  IncomeCategory.SALARY,
  IncomeCategory.BONUS,
  IncomeCategory.FREELANCE,
  IncomeCategory.BUSINESS,
  IncomeCategory.INVESTMENT,
  IncomeCategory.RENTAL,
  IncomeCategory.OTHER,
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatBaht(n: number) {
  return `฿${n.toLocaleString("en-US")}`;
}

const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const CURRENT_MONTH = now.getMonth() + 1;
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - 2 + i);

export default function RecurringIncomeModal() {
  const { modalOpen, editingItem, closeModal, saveItem } =
    useRecurringIncomeStore();

  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState<RecurringFrequency>(
    RecurringFrequency.MONTHLY,
  );
  const [dayOfMonth, setDayOfMonth] = useState("1");
  const [applyMonth, setApplyMonth] = useState("1");
  const [taxIncomeType, setTaxIncomeType] = useState<TaxIncomeType>(
    TaxIncomeType.Employment40_1,
  );
  const [incomeCategory, setIncomeCategory] = useState<IncomeCategory | null>(
    null,
  );
  const [senderPattern, setSenderPattern] = useState("");
  const [accountId, setAccountId] = useState("");
  const accounts = useAccountStore((s) => s.accounts);
  const ensureAccounts = useAccountStore((s) => s.ensureAccounts);
  const [startMonth, setStartMonth] = useState(CURRENT_MONTH);
  const [startYear, setStartYear] = useState(CURRENT_YEAR);
  const [endMonth, setEndMonth] = useState(12);
  const [endYear, setEndYear] = useState(CURRENT_YEAR);
  const [isOngoing, setIsOngoing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editingItem) {
      setTitle(editingItem.title);
      setAmount(String(editingItem.amount));
      setFrequency(editingItem.frequency as RecurringFrequency);
      setDayOfMonth(String(editingItem.dayOfMonth));
      setApplyMonth(String(editingItem.applyMonth));
      setTaxIncomeType(
        editingItem.taxIncomeType ?? TaxIncomeType.Employment40_1,
      );
      setIncomeCategory(editingItem.incomeCategory ?? null);
      setSenderPattern(editingItem.senderPattern ?? "");
      setAccountId(editingItem.accountId ?? "");
      setStartMonth(editingItem.startMonth ?? CURRENT_MONTH);
      setStartYear(editingItem.startYear ?? CURRENT_YEAR);
      const hasEnd =
        editingItem.endMonth != null && editingItem.endYear != null;
      setIsOngoing(!hasEnd);
      setEndMonth(editingItem.endMonth ?? 12);
      setEndYear(editingItem.endYear ?? CURRENT_YEAR);
    } else {
      setTitle("");
      setAmount("");
      setFrequency(RecurringFrequency.MONTHLY);
      setDayOfMonth("1");
      setApplyMonth("1");
      setTaxIncomeType(TaxIncomeType.Employment40_1);
      setIncomeCategory(null);
      setSenderPattern("");
      setAccountId("");
      setStartMonth(CURRENT_MONTH);
      setStartYear(CURRENT_YEAR);
      setEndMonth(12);
      setEndYear(CURRENT_YEAR);
      setIsOngoing(true);
    }
    setError(null);
  }, [editingItem, modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;
    async function load() {
      try {
        await ensureAccounts();
      } catch {
        // The picker is optional; the form works without accounts.
      }
    }
    void load();
  }, [modalOpen, ensureAccounts]);

  if (!modalOpen) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const amountNum = parseFloat(amount);
    if (!title.trim() || isNaN(amountNum) || amountNum < 1) {
      setError("Title and a valid amount are required.");
      return;
    }
    const dayNum = parseInt(dayOfMonth, 10);
    if (isNaN(dayNum) || dayNum < 1 || dayNum > 28) {
      setError("Day of month must be between 1 and 28.");
      return;
    }
    if (!isOngoing) {
      const startTotal = startYear * 12 + startMonth;
      const endTotal = endYear * 12 + endMonth;
      if (endTotal < startTotal) {
        setError("Active until must be the same as or after Active from.");
        return;
      }
    }

    const payload: CreateRecurringIncomePayload = {
      title: title.trim(),
      amount: amountNum,
      frequency,
      dayOfMonth: dayNum,
      taxIncomeType,
      incomeCategory: incomeCategory ?? null,
      senderPattern: senderPattern.trim() || null,
      accountId: accountId || null,
      ...(frequency === RecurringFrequency.YEARLY && {
        applyMonth: parseInt(applyMonth, 10),
      }),
      startMonth,
      startYear,
      endMonth: isOngoing ? null : endMonth,
      endYear: isOngoing ? null : endYear,
    };

    setSaving(true);
    setError(null);
    try {
      await saveItem(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const selectClass =
    "min-w-0 rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-2 py-2 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-border";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeModal();
      }}
    >
      <div className="w-full max-w-sm mx-4 rounded-[16px] bg-card border-[0.5px] border-border shadow-xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[15px] font-semibold text-foreground">
            {editingItem ? "Edit recurring income" : "Add recurring income"}
          </h2>
          <button
            onClick={closeModal}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Title */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Title
            </label>
            <input
              className="w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border"
              placeholder="e.g. Salary"
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Amount */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Amount (฿)
            </label>
            <AmountInput
              className="w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border [font-variant-numeric:tabular-nums]"
              placeholder="50000"
              value={amount}
              onChange={setAmount}
            />
            {amount && !isNaN(parseFloat(amount)) && (
              <span className="text-[11px] text-muted-foreground">
                {formatBaht(parseFloat(amount))}
              </span>
            )}
          </div>

          {/* Tax income type */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Tax income type
            </label>
            <div className="flex gap-2">
              {(
                [
                  TaxIncomeType.Employment40_1,
                  TaxIncomeType.Freelance40_8,
                ] as const
              ).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTaxIncomeType(t)}
                  className={`flex-1 rounded-[8px] border-[0.5px] py-2 text-[12px] font-medium transition-colors cursor-pointer ${
                    taxIncomeType === t
                      ? "border-foreground bg-foreground text-[var(--page-bg)]"
                      : "border-border bg-[var(--surface-muted)] text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t === TaxIncomeType.Employment40_1
                    ? "40(1) Salary"
                    : "40(8) Freelance"}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {taxIncomeType === TaxIncomeType.Employment40_1
                ? "Salary, wages, bonuses — 50% expense deduction"
                : "Business, professional, freelance — 60% deduction"}
            </p>
          </div>

          {/* Income category */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Income type
            </label>
            <div className="flex flex-wrap gap-1.5">
              {INCOME_CATEGORIES.map((cat) => {
                const selected = incomeCategory === cat;
                const color = INCOME_CATEGORY_COLORS[cat];
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setIncomeCategory(selected ? null : cat)}
                    className="flex items-center gap-1.5 rounded-[6px] px-2.5 py-1 text-[12px] font-medium border transition-all cursor-pointer"
                    style={
                      selected
                        ? {
                            background: `var(--cat-${cat.toLowerCase()}-bg, #E1F5EE)`,
                            color: `var(--cat-${cat.toLowerCase()}-text, #0F6E56)`,
                            borderColor: color,
                          }
                        : {
                            background: "transparent",
                            color: "var(--muted-foreground)",
                            borderColor: "var(--border)",
                          }
                    }
                  >
                    <span
                      className="w-2 h-2 rounded-[2px]"
                      style={{ background: color }}
                    />
                    {INCOME_CATEGORY_LABELS[cat]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Statement sender */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Statement sender (optional)
            </label>
            <input
              className="w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border"
              placeholder="e.g. จาก SMART DBBK X5001 IBM"
              maxLength={200}
              value={senderPattern}
              onChange={(e) => setSenderPattern(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">
              Bank statement deposits whose sender contains this text become
              this income, with the real amount.
            </p>
          </div>

          {/* Account */}
          {accounts.some((a) => a.isActive) && (
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] text-muted-foreground font-medium">
                Account (optional)
              </label>
              <select
                className="w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-border"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                <option value="">Unassigned</option>
                {accounts
                  .filter((a) => a.isActive || a.id === accountId)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {a.digits ? ` ••${a.digits}` : ""}
                    </option>
                  ))}
              </select>
              <p className="text-[11px] text-muted-foreground">
                Applied income is credited to this account.
              </p>
            </div>
          )}

          {/* Frequency */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Frequency
            </label>
            <div className="flex gap-2">
              {(
                [RecurringFrequency.MONTHLY, RecurringFrequency.YEARLY] as const
              ).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFrequency(f)}
                  className={`flex-1 rounded-[8px] border-[0.5px] py-2 text-[12px] font-medium transition-colors cursor-pointer ${
                    frequency === f
                      ? "border-foreground bg-foreground text-[var(--page-bg)]"
                      : "border-border bg-[var(--surface-muted)] text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {f.charAt(0) + f.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Day of month */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Day of month credited (1–28)
            </label>
            <input
              type="number"
              min={1}
              max={28}
              className="w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-border [font-variant-numeric:tabular-nums]"
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(e.target.value)}
            />
          </div>

          {/* Apply month (yearly only) */}
          {frequency === RecurringFrequency.YEARLY && (
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] text-muted-foreground font-medium">
                Which month
              </label>
              <select
                className={`w-full ${selectClass}`}
                value={applyMonth}
                onChange={(e) => setApplyMonth(e.target.value)}
              >
                {MONTH_NAMES.map((name, i) => (
                  <option key={i + 1} value={String(i + 1)}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Active date range */}
          <div className="flex flex-col gap-2 pt-1 border-t-[0.5px] border-border">
            <label className="text-[12px] text-muted-foreground font-medium pt-1">
              Active period
            </label>

            <div className="flex items-center gap-2">
              <span className="text-[12px] text-muted-foreground w-10 sm:w-16 shrink-0">
                From
              </span>
              <select
                className={`flex-1 ${selectClass}`}
                value={startMonth}
                onChange={(e) => setStartMonth(Number(e.target.value))}
              >
                {MONTH_NAMES.map((name, i) => (
                  <option key={i + 1} value={i + 1}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                className={`w-20 shrink-0 ${selectClass}`}
                value={startYear}
                onChange={(e) => setStartYear(Number(e.target.value))}
              >
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[12px] text-muted-foreground w-10 sm:w-16 shrink-0">
                Until
              </span>
              {isOngoing ? (
                <span className="flex-1 text-[13px] text-muted-foreground italic">
                  Ongoing
                </span>
              ) : (
                <>
                  <select
                    className={`flex-1 ${selectClass}`}
                    value={endMonth}
                    onChange={(e) => setEndMonth(Number(e.target.value))}
                  >
                    {MONTH_NAMES.map((name, i) => (
                      <option key={i + 1} value={i + 1}>
                        {name}
                      </option>
                    ))}
                  </select>
                  <select
                    className={`w-20 shrink-0 ${selectClass}`}
                    value={endYear}
                    onChange={(e) => setEndYear(Number(e.target.value))}
                  >
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </>
              )}
              <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={isOngoing}
                  onChange={(e) => setIsOngoing(e.target.checked)}
                  className="w-3.5 h-3.5 accent-foreground cursor-pointer"
                />
                Ongoing
              </label>
            </div>
          </div>

          {error && <p className="text-[12px] text-red-500">{error}</p>}

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-1.5 w-full rounded-[8px] bg-foreground text-[var(--page-bg)] py-2.5 text-[13px] font-semibold hover:opacity-80 transition-opacity disabled:opacity-40 cursor-pointer"
          >
            {saving && <Spinner className="size-3.5" />}
            {saving ? "Saving…" : editingItem ? "Save changes" : "Add income"}
          </button>
        </form>
      </div>
    </div>
  );
}
